import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTownHallByAny,
  selectCurrentTownHall,
} from "@/store/redux/slices/townhall/townhall_slice";
import { fetchMultipleUsersAsync } from "@/store/redux/slices/user/user_slice";
import Back from "@/components/specific/Back";
import HeaderImage from "@/pages/town_hall/components/single_town_hall_details/Header_Image";
import EventInfo from "@/pages/town_hall/components/single_town_hall_details/Event_Info";
import EventTabs from "@/pages/town_hall/components/single_town_hall_details/Event_Tab";
import ActivityList from "@/pages/town_hall/components/single_town_hall_details/Activity_List";
import UserProfileCard from "@/ui/User_Profile_Card";
import LocationMap from "@/pages/town_hall/components/single_town_hall_details/Location_Map";
import ActionButtons from "@/pages/town_hall/components/single_town_hall_details/Action_Button";
import Success from "@/components/common/alerts/Success";
import SpeakersTab from "@/pages/town_hall/components/single_town_hall_details/Speakers_Tab";
import AttendeesTab from "@/pages/town_hall/components/single_town_hall_details/Attendees_Tab";
import { useLoader } from "@/hooks/LoaderProvider";
import { selectUserProfile } from "@/store/redux/slices/user/user_slice";
import Breadcrumb from "@/components/specific/Breadcrumb";
import { fetchOrganizationById } from "@/store/redux/slices/kyc/kyc_setup_slice";
import { calculateKycStatus } from "@/utils/kyc_utils";

const SingleTownHall = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();
  const [activeTab, setActiveTab] = useState("schedules");
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [speakers, setSpeakers] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const userProfile = useSelector(selectUserProfile);

  const townHall = useSelector(selectCurrentTownHall);
  const [kycStatus, setKycStatus] = useState({
    completionPercentage: 100,
    completedSteps: 3,
  });
  const organizationData = useSelector(
    (state) => state.organization.organizationData
  );

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        try {
          showLoader(); // Show loader before fetching data
          const result = await dispatch(
            fetchTownHallByAny({ key: "_id", value: id })
          ).unwrap();

          // Get the town hall data from the first item in the data array
          const townHallData = result.data?.[0];

          if (!townHallData) {
            console.error("Town hall data not found");
            return;
          }

          if (townHallData.speakers?.length) {
            const speakersData = await dispatch(
              fetchMultipleUsersAsync(townHallData.speakers)
            ).unwrap();
            setSpeakers(speakersData);
          }

          if (townHallData.attendees?.length) {
            const attendeesData = await dispatch(
              fetchMultipleUsersAsync(townHallData.attendees)
            ).unwrap();
            setAttendees(attendeesData);
          }
        } catch (error) {
          console.error("Error fetching town hall data:", error);
        } finally {
          hideLoader(); // Hide loader after all data is fetched
        }
      }
    };
    fetchData();
  }, [dispatch, hideLoader, id, showLoader]);

  useEffect(() => {
    // Fetch organization data for KYC completion
    dispatch(fetchOrganizationById());
  }, [dispatch]);

  useEffect(() => {
    if (organizationData) {
      const status = calculateKycStatus(organizationData);
      setKycStatus(status);
    }
  }, [organizationData]);

  const handleDeleteSuccess = () => {
    setShowDeleteSuccess(true);
  };

  const handleSuccessClose = () => {
    setShowDeleteSuccess(false);
    navigate("/dashboard/townhall");
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  if (!townHall) {
    return null;
  }

  // Update tab items with new tabs
  const tabItems = [
    {
      key: "schedules",
      label: "Event Schedules",
      children: (
        <ActivityList
          activities={[
            {
              id: 1,
              time: townHall.duration.startTime,
              duration: `${townHall.duration.startTime} - ${townHall.duration.endTime}`,
              title: townHall.title,
            },
          ]}
        />
      ),
    },
    {
      key: "speakers",
      label: "Speakers",
      children: <SpeakersTab speakers={speakers} />,
    },
    {
      key: "attendees",
      label: "Attendees",
      children: <AttendeesTab attendees={attendees} />,
    },
  ];

  const breadcrumbItems = [
    { title: "Townhall", link: "/dashboard/townhall" },
    { title: townHall?.title || "Townhall Details", link: null },
  ];

  return (
    <div className="w-full py-6">
      <div className="mb-4">
        <Breadcrumb items={breadcrumbItems} />
        <Back text="Back" />
      </div>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="col-span-2">
          <HeaderImage image={townHall.fileUrl} />
          <div className="lg:border-2 border-[#eee] rounded-lg p-4">
            <EventInfo
              date={new Date(townHall.duration.startDate)
                .toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })
                .toUpperCase()}
              time={`${townHall.duration.startTime} PROMPT`}
              title={townHall.title}
              description={townHall.description}
              participants={[...speakers, ...attendees].map((user, index) => ({
                id: `${user?._id}-${index}`,
                avatar: user?.profileImage,
              }))}
            />
            <EventTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              items={tabItems}
            />
          </div>
        </div>
        <div>
          <div className="lg:border-2 border-[#eee] lg:mb-9 rounded-lg p-4">
            <UserProfileCard
              user={{
                name: userProfile.fullname,
                customRole: "Organizer",
                avatar: speakers[0]?.profileImage || userProfile.profileImage,
                badgeCount:
                  kycStatus.completedSteps > 0
                    ? kycStatus.completedSteps
                    : null,
              }}
              completionPercentage={kycStatus.completionPercentage}
            />
          </div>
          <LocationMap
            location={{
              address:
                townHall.location?.address ||
                townHall.location ||
                "No address provided",
              country: "Nigeria", // Or get from your townHall data
              virtualLink: townHall.virtualMeetingUrl,
              coordinates: townHall.location?.coordinates || {
                lat: 6.5244, // Default to Lagos
                lng: 3.3792,
              },
            }}
          />
          <ActionButtons
            townHall={townHall}
            onDeleteSuccess={handleDeleteSuccess}
          />
        </div>
      </div>

      {/* Success Modal */}
      <Success
        isVisible={showDeleteSuccess}
        onClose={handleSuccessClose}
        header="Town Hall Deleted"
        paragraph="The town hall has been successfully deleted."
        buttonText="Done"
        showCancelButton={false}
        onButtonClick={handleSuccessClose}
      />
    </div>
  );
};

export default SingleTownHall;
